import React, { useMemo, useState } from "react";
import {
    Search,
    ChevronDown,
    Tag,
    Percent,
    Calendar,
    Users,
    TrendingUp,
    Copy,
} from "lucide-react";

const PROMOTIONS = [
    {
        id: "PROMO-01",
        code: "APIC2026",
        title: "New Year Prototyping Offer",
        type: "Percentage",
        value: "20% off",
        centre: "All Centres",
        startDate: "01 Jan 2026",
        endDate: "31 Jan 2026",
        usage: 184,
        limit: 500,
        status: "Active",
    },
    {
        id: "PROMO-02",
        code: "FIRSTBOOK",
        title: "First Machine Booking Discount",
        type: "Flat",
        value: "₹500 off",
        centre: "Vizag",
        startDate: "10 Jun 2026",
        endDate: "10 Aug 2026",
        usage: 76,
        limit: 200,
        status: "Active",
    },
    {
        id: "PROMO-03",
        code: "STUDENT15",
        title: "Student Innovator Discount",
        type: "Percentage",
        value: "15% off",
        centre: "Vijayawada",
        startDate: "01 May 2026",
        endDate: "30 Jun 2026",
        usage: 312,
        limit: 300,
        status: "Expired",
    },
    {
        id: "PROMO-04",
        code: "EVSPRINT",
        title: "EV Prototyping Sprint Bundle",
        type: "Flat",
        value: "₹2000 off",
        centre: "Tirupati",
        startDate: "15 Jul 2026",
        endDate: "15 Sep 2026",
        usage: 22,
        limit: 100,
        status: "Active",
    },
    {
        id: "PROMO-05",
        code: "AGRITECH10",
        title: "AgriTech Innovators Offer",
        type: "Percentage",
        value: "10% off",
        centre: "Kurnool",
        startDate: "20 Jul 2026",
        endDate: "20 Aug 2026",
        usage: 0,
        limit: 150,
        status: "Scheduled",
    },
    {
        id: "PROMO-06",
        code: "WELCOME50",
        title: "Welcome Bonus for New Users",
        type: "Flat",
        value: "₹300 off",
        centre: "All Centres",
        startDate: "01 Jun 2026",
        endDate: "01 Jul 2026",
        usage: 410,
        limit: 400,
        status: "Paused",
    },
];

const STATUS_STYLES = {
    Active: "bg-green-100 text-green-700",
    Scheduled: "bg-blue-100 text-blue-700",
    Paused: "bg-amber-100 text-amber-700",
    Expired: "bg-gray-100 text-gray-500",
};

function StatCard({ icon: Icon, label, value, tone }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-500">{label}</p>
            </div>
        </div>
    );
}

export default function PromotionsDashboard() {
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("All Status");
    const [typeFilter, setTypeFilter] = useState("All Types");
    const [copiedCode, setCopiedCode] = useState(null);

    const statuses = useMemo(
        () => ["All Status", ...new Set(PROMOTIONS.map((p) => p.status))],
        []
    );
    const types = useMemo(
        () => ["All Types", ...new Set(PROMOTIONS.map((p) => p.type))],
        []
    );

    const counts = useMemo(() => {
        const active = PROMOTIONS.filter((p) => p.status === "Active").length;
        const totalUsage = PROMOTIONS.reduce((sum, p) => sum + p.usage, 0);
        const avgRedemption = Math.round(
            (PROMOTIONS.reduce((sum, p) => sum + p.usage / p.limit, 0) / PROMOTIONS.length) * 100
        );
        return { total: PROMOTIONS.length, active, totalUsage, avgRedemption };
    }, []);

    const filtered = useMemo(() => {
        return PROMOTIONS.filter((p) => {
            const matchesQuery =
                query.trim() === "" ||
                [p.code, p.title, p.centre].some((field) =>
                    field.toLowerCase().includes(query.trim().toLowerCase())
                );
            const matchesStatus = statusFilter === "All Status" || p.status === statusFilter;
            const matchesType = typeFilter === "All Types" || p.type === typeFilter;
            return matchesQuery && matchesStatus && matchesType;
        });
    }, [query, statusFilter, typeFilter]);

    const handleCopy = (code) => {
        navigator.clipboard?.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 1500);
    };

    return (
        <div className="h-screen w-full overflow-y-auto bg-gray-50 scrollbar-hide">
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="p-6">
                {/* Page heading */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Promotions</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Manage discount codes and campaigns across all centres
                    </p>
                </div>

                {/* Stat cards */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard icon={Tag} label="Total promotions" value={counts.total} tone="bg-indigo-50 text-indigo-500" />
                    <StatCard icon={Percent} label="Active" value={counts.active} tone="bg-green-50 text-green-500" />
                    <StatCard icon={Users} label="Total redemptions" value={counts.totalUsage} tone="bg-blue-50 text-blue-500" />
                    <StatCard icon={TrendingUp} label="Avg. redemption rate" value={`${counts.avgRedemption}%`} tone="bg-purple-50 text-purple-500" />
                </div>

                {/* Table card */}
                <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {/* Header */}
                    <div className="flex flex-col gap-4 border-b border-gray-100 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Active campaigns</h2>
                            <p className="mt-1 text-sm text-gray-500">
                                {filtered.length} of {PROMOTIONS.length} promotions
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search code, title, centre..."
                                    className="w-64 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-gray-300 focus:bg-white"
                                />
                            </div>

                            <div className="relative">
                                <select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {types.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>

                            <div className="relative">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {statuses.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1150px] table-fixed border-collapse text-left">
                            <thead>
                                <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                                    <th className="w-[24%] px-6 py-3 font-medium">Promotion</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Code</th>
                                    <th className="w-[10%] px-6 py-3 font-medium">Discount</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Centre</th>
                                    <th className="w-[16%] px-6 py-3 font-medium">Validity</th>
                                    <th className="w-[14%] px-6 py-3 font-medium">Usage</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((p, idx) => (
                                    <tr
                                        key={p.id}
                                        className={`text-sm ${idx !== filtered.length - 1 ? "border-b border-gray-100" : ""}`}
                                    >
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-gray-900">{p.title}</p>
                                            <p className="mt-0.5 text-xs text-gray-400">{p.id}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => handleCopy(p.code)}
                                                className="flex items-center gap-1.5 rounded-lg border border-dashed border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-100"
                                            >
                                                {p.code}
                                                <Copy className="h-3 w-3" />
                                            </button>
                                            {copiedCode === p.code && (
                                                <span className="mt-1 block text-[11px] text-green-600">Copied!</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-900">{p.value}</td>
                                        <td className="px-6 py-4 text-gray-600">{p.centre}</td>
                                        <td className="px-6 py-4 text-gray-500">
                                            <div className="flex items-center gap-1.5">
                                                <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
                                                <span className="truncate">{p.startDate} – {p.endDate}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 w-32">
                                                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                                                    <div
                                                        className="h-full rounded-full bg-indigo-500"
                                                        style={{ width: `${Math.min(100, Math.round((p.usage / p.limit) * 100))}%` }}
                                                    />
                                                </div>
                                                <span className="w-14 shrink-0 text-xs text-gray-500">
                                                    {p.usage}/{p.limit}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[p.status]}`}>
                                                {p.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}

                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-400">
                                            No promotions match your filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}